import { FC, useMemo } from 'react';
import {
	DashboardPerformerSkeleton,
	DashboardPerformersStats,
	StatSparklineChart,
	StatSparklineChartSkeleton,
	DashboardCellRendererFrame,
	NoValuePlaceholder,
} from '@aleph/shared-components';
import useDashboardStats from 'wb-spotify/app/routes/superGrid/hooks/useDashboardStats';
import { SpotifyDateStatsFragmentFragment } from 'wb-spotify/graphql/generated';
import { LEVEL_ID_FILTER } from 'wb-spotify/app/routes/superGrid/actionRow/filters/utils';
import { ICellRendererParams } from 'ag-grid-community';
import { useParams, useNavigate } from 'react-router-dom';
import {
	getColorWithoutHSL,
	getCssVariable,
	LEVEL_COLORS,
} from 'wb-spotify/app/routes/superGrid/actionRow/charts/utils';
import { SpotifyLevel } from 'wb-spotify/app/routes/superGrid/utils';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';

const StatCellRenderer: FC<ICellRendererParams & {
	currency?: string,
	currencyDivisor?: number,
	fractionDigits?: number
	isRate?: boolean,
}> = (params) => {
	const isDashboardView = params.data?.isDashboardView && !params.node.expanded;

	const focusedCell = params.api?.getFocusedCell();
	const isFocusedOnThisRow = focusedCell?.rowIndex === params.rowIndex;
	const isFocused = isFocusedOnThisRow && focusedCell?.column === params.column;

	const navigate = useNavigate();

	const { lifetimeStats } = useSupergridSettings();

	const {
		reloading, loading, stats, worstPerformers, bestPerformers,
	} = useDashboardStats({
		parentFilter: {
			[LEVEL_ID_FILTER[params.data?.level ?? '']]: [{ value: [params.data?.id] }],
		},
		focusedCell: isFocusedOnThisRow ? focusedCell : null,
		selectedKPI: params?.data?.clientKpi,
		skip:        !isDashboardView,
	});

	const fieldName = params.colDef?.field ?? '';

	const dividedStats = stats?.map((statEntry) => ({
		...statEntry,
		[fieldName]: statEntry[fieldName] / (params.currencyDivisor || 1),
	}));

	const dividedBestPerformers = bestPerformers?.map((performer) => ({
		...performer,
		currStats: performer?.currStats?.map((statEntry) => ({
			...statEntry,
			[fieldName]: (statEntry?.[fieldName] ?? 0) / (params.currencyDivisor || 1),
		})) ?? [],
		lastStats: performer?.lastStats?.map((statEntry) => ({
			...statEntry,
			[fieldName]: (statEntry?.[fieldName] ?? 0) / (params.currencyDivisor || 1),
		})) ?? [],
	}));

	const divideWorstPerformers = worstPerformers?.map((performer) => ({
		...performer,
		currStats: performer?.currStats?.map((statEntry) => ({
			...statEntry,
			[fieldName]: (statEntry?.[fieldName] ?? 0) / (params.currencyDivisor || 1),
		})) ?? [],
		lastStats: performer?.lastStats?.map((statEntry) => ({
			...statEntry,
			[fieldName]: (statEntry?.[fieldName] ?? 0) / (params.currencyDivisor || 1),
		})) ?? [],
	}));

	const formattedValue = useMemo(() => {
		if (params.value == null || params.value === '') return null;
		const num = Number(params.value);

		return Number.isInteger(num) ? String(num) : num.toFixed(2);
	}, [params.value]);

	return (
		<DashboardCellRendererFrame>
			<div className="flex items-center justify-between h-full px-[8px] gap-[8px]">
				{!!(params.value && params.currency) && <div className="text-gray-700 text-[12px]">{params.currency}</div>}
				{!!(params.value && params.isRate) && <div className="text-gray-700 text-[12px]">%</div>}
				{!(params.value && params.isRate) && <div/>}

				{formattedValue ?? <NoValuePlaceholder />}
			</div>

			{
				isDashboardView
				&& <div
					className="p-[12px] max-h-full h-full"
					key={params.column?.getActualWidth()}
				>
					{
						!lifetimeStats && (
							<>
								{loading && <StatSparklineChartSkeleton />}
								{
									stats && !loading
						&& <StatSparklineChart
							// @ts-ignore: TODO: fix type
							data={dividedStats}
							// @ts-ignore: TODO: fix type
							field={
								(params.colDef?.field ?? 'listeners') as keyof SpotifyDateStatsFragmentFragment
							}
							color={`hsl(${getCssVariable(getColorWithoutHSL(LEVEL_COLORS[SpotifyLevel.AD_ACCOUNT][0]))})`}
							navigate={navigate}
							useParams={useParams}
						/>
								}
							</>
						)
					}
				</div>
			}

			{
				isDashboardView
				&& <div className="w-full h-full">
					{
						(reloading && isFocused)
						&& <DashboardPerformerSkeleton />
					}

					{
						dividedBestPerformers
					&& <DashboardPerformersStats
						field={fieldName}
						isRate={params.isRate}
						data={dividedBestPerformers}
					/>
					}
				</div>
			}

			{
				isDashboardView
				&& <div className="w-full h-full">
					{
						(reloading && isFocused)
						&& <DashboardPerformerSkeleton />
					}

					{
						divideWorstPerformers
						&& <DashboardPerformersStats
							field={fieldName}
							isRate={params.isRate}
							data={divideWorstPerformers}
						/>
					}
				</div>
			}
		</DashboardCellRendererFrame>
	);
};

export default StatCellRenderer;
